import React, { useEffect, useRef, useState } from 'react';
import { Pipette, Plus, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const CHECKER = 'repeating-conic-gradient(#ccc 0% 25%, #fff 0% 50%) 50% / 12px 12px';

function hexToRgb(hex) {
  const h = (hex || '').replace('#', '');
  return { r: parseInt(h.slice(0, 2), 16) || 0, g: parseInt(h.slice(2, 4), 16) || 0, b: parseInt(h.slice(4, 6), 16) || 0 };
}
function toHex2(n) { return Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, '0'); }

export function parseColor(v) {
  if (!v || v === 'transparent') return { hex: '#000000', a: v === 'transparent' ? 0 : 1 };
  const s = String(v).trim();
  const m = s.match(/rgba?\(([^)]+)\)/i);
  if (m) {
    const p = m[1].split(',').map((x) => x.trim());
    const hex = `#${toHex2(+p[0])}${toHex2(+p[1])}${toHex2(+p[2])}`;
    return { hex, a: p[3] !== undefined ? Math.max(0, Math.min(1, +p[3])) : 1 };
  }
  if (s[0] === '#') {
    if (s.length === 9) return { hex: `#${s.slice(1, 7)}`, a: (parseInt(s.slice(7, 9), 16) || 0) / 255 };
    if (s.length === 7) return { hex: s, a: 1 };
    if (s.length === 4) return { hex: `#${s[1]}${s[1]}${s[2]}${s[2]}${s[3]}${s[3]}`, a: 1 };
  }
  return { hex: '#000000', a: 1 };
}

export function composeColor(hex, a) {
  if (a >= 1) return hex;
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${Number(a.toFixed(2))})`;
}

export default function AlphaColorPicker({ value, onChange, allowTransparent = true, testId }) {
  const { getBrandColors, saveBrandColors } = useAuth();
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState(null);
  const [presets, setPresets] = useState([]);
  const ref = useRef(null);
  const btnRef = useRef(null);
  const { hex, a } = parseColor(value);

  useEffect(() => { if (open) getBrandColors().then((d) => setPresets(d.colors || [])).catch(() => {}); }, [open, getBrandColors]);

  const savePreset = async () => {
    const cur = composeColor(hex, a);
    if (presets.some((p) => p.toLowerCase() === cur.toLowerCase())) return;
    const next = [...presets, cur];
    setPresets(next);
    try { const d = await saveBrandColors(next); setPresets(d.colors || next); } catch (e) { /* ignore */ }
  };
  const removePreset = async (c) => {
    const next = presets.filter((p) => p !== c);
    setPresets(next);
    try { await saveBrandColors(next); } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target) && btnRef.current && !btnRef.current.contains(e.target)) setOpen(false); };
    const onScroll = () => setOpen(false);
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open]);

  const POP_W = 208;
  const POP_H = 250;
  const toggle = () => {
    if (open) { setOpen(false); return; }
    if (btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      const left = Math.max(8, Math.min(r.right - POP_W, window.innerWidth - POP_W - 8));
      let top = r.bottom + 6;
      if (top + POP_H > window.innerHeight - 8) {
        top = Math.max(8, r.top - POP_H - 6);  // flip above the swatch when it would overflow the bottom
      }
      setCoords({ top, left });
    }
    // opening: any other open picker closes itself via its own outside-mousedown handler
    setOpen(true);
  };

  const display = composeColor(hex, a);
  const setHex = (h) => onChange(composeColor(h, a === 0 ? 1 : a));
  const setAlpha = (pct) => onChange(composeColor(hex, pct / 100));
  const supportsEyeDropper = typeof window !== 'undefined' && 'EyeDropper' in window;
  const pickEyedropper = async () => {
    try {
      const res = await new window.EyeDropper().open();
      if (res && res.sRGBHex) setHex(res.sRGBHex);
    } catch (e) { /* user cancelled */ }
  };

  return (
    <div className="relative inline-block">
      <button
        type="button"
        ref={btnRef}
        onClick={toggle}
        data-testid={testId}
        title="Choose colour and transparency"
        className="w-10 h-9 rounded border cursor-pointer"
        style={{ borderColor: 'rgba(0,0,0,0.2)', background: CHECKER, padding: 0, overflow: 'hidden' }}
      >
        <span className="block w-full h-full" style={{ background: display }} />
      </button>
      {open && coords && (
        <div
          ref={ref}
          className="p-3 rounded-lg shadow-lg bg-white"
          style={{ position: 'fixed', top: coords.top, left: coords.left, width: POP_W, zIndex: 1000, border: '1px solid rgba(0,0,0,0.15)' }}
          data-testid={testId ? `${testId}-popover` : undefined}
        >
          <div className="flex items-center gap-2 mb-3">
            <input
              type="color"
              value={hex}
              onChange={(e) => setHex(e.target.value)}
              data-testid={testId ? `${testId}-hue` : undefined}
              className="w-10 h-9 rounded cursor-pointer border p-0"
              style={{ borderColor: 'rgba(0,0,0,0.15)' }}
            />
            <span className="w-8 h-8 rounded border flex-shrink-0" style={{ borderColor: 'rgba(0,0,0,0.15)', background: CHECKER }}>
              <span className="block w-full h-full rounded" style={{ background: display }} />
            </span>
            <span className="text-xs font-mono" style={{ color: 'var(--olive)' }}>{Math.round(a * 100)}%</span>
            {supportsEyeDropper && (
              <button type="button" onClick={pickEyedropper} title="Pick a colour from the screen" data-testid={testId ? `${testId}-eyedropper` : undefined} className="ml-auto p-1.5 rounded border" style={{ borderColor: 'rgba(0,0,0,0.15)', color: 'var(--navy)' }}>
                <Pipette size={15} />
              </button>
            )}
          </div>
          <label className="block text-xs mb-1" style={{ color: 'var(--olive)' }}>Transparency</label>
          <input
            type="range"
            min="0"
            max="100"
            value={Math.round(a * 100)}
            onChange={(e) => setAlpha(Number(e.target.value))}
            data-testid={testId ? `${testId}-alpha` : undefined}
            className="w-full cursor-pointer"
            style={{ accentColor: 'var(--maroon)' }}
          />
          {allowTransparent && (
            <button
              type="button"
              onClick={() => { onChange('transparent'); }}
              data-testid={testId ? `${testId}-transparent` : undefined}
              className="mt-2 w-full text-xs py-1.5 rounded"
              style={{ border: '1px solid rgba(0,0,0,0.15)', color: 'var(--navy)', background: a === 0 ? 'var(--sage)' : 'white' }}
            >
              Fully transparent
            </button>
          )}
          <div className="mt-3 pt-2 border-t" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs" style={{ color: 'var(--olive)' }}>Brand swatches</span>
              <button type="button" onClick={savePreset} title="Save current colour" data-testid={testId ? `${testId}-save-preset` : undefined} className="inline-flex items-center gap-0.5 text-xs px-1.5 py-0.5 rounded" style={{ border: '1px solid rgba(0,0,0,0.15)', color: 'var(--navy)' }}><Plus size={12} /> Save</button>
            </div>
            {presets.length === 0 ? (
              <p className="text-[11px]" style={{ color: 'var(--olive)' }}>No saved swatches yet.</p>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {presets.map((c) => (
                  <span key={c} className="relative group" data-testid={testId ? `${testId}-preset` : undefined}>
                    <button type="button" onClick={() => onChange(c)} title={c} className="w-6 h-6 rounded border" style={{ borderColor: 'rgba(0,0,0,0.2)', background: CHECKER, padding: 0, overflow: 'hidden' }}>
                      <span className="block w-full h-full" style={{ background: c }} />
                    </button>
                    <button type="button" onClick={() => removePreset(c)} title="Remove" className="absolute -top-1.5 -right-1.5 hidden group-hover:flex items-center justify-center w-3.5 h-3.5 rounded-full" style={{ background: '#8a1f11', color: '#fff' }}><X size={9} /></button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
